/* eslint-disable complexity */

'use client'

import type { LegacyRef, ReactNode } from 'react'
import { Box, Stack, Typography, Chip, type SxProps } from '@mui/material'
import { RESOURCE_TYPE } from '@models'
import { Image, ImageData, Icon } from '@media-components'
import {
  ActionButton,
  ActionButtonData,
  BreadcrumbsNav,
  BreadcrumbsNavPath,
  ChipData,
  LinkClick,
  SectionHeader,
  SectionTag,
  SectionTagData,
  SectionWrapper,
  SectionWrapperProps,
  StatsCardData,
  FormComponent,
  type CardTypes,
  type GlowProps,
  DocumentRenderer,
  DynamicFormRendererData,
  DocumentRendererData,
} from '@components/common'
import {
  StandardCard,
  StandardCardData,
} from '@components/common/Cards/StandardCard'
import { ReachOutForm } from '@components/entities/ReachOutForm'
import { portalUrl } from '@utils/environment'
import { publicImages } from '@utils/shared'
import { commonStyles, CustomStyles, getStyles } from '@utils/styles'
import { MobileOnlyCardsSlider } from './CardsSlider'
import defaultStyles, { StylesClassNames } from './styles'

// FIXME: Add Social Media Links. This needs to be removed later once query is added for this

type SocialMediaData = {
  imageData: ImageData
  link: string
}

const socials = [
  {
    imageData: {
      src: publicImages.facebookNew,
      alt: 'img',
    },
    link: 'https://www.facebook.com/people/Costimizer/61580509533722/',
  },

  {
    imageData: {
      src: publicImages.instaNew,
      alt: 'img',
    },
    link: 'https://www.instagram.com/costimizer.ai/',
  },
  {
    imageData: {
      src: publicImages.linkedinNew,
      alt: 'img',
    },
    link: 'https://www.linkedin.com/company/108379575/',
  },
  {
    imageData: {
      src: publicImages.youtubeNew,
      alt: 'img',
    },
    link: 'https://www.youtube.com/@costimizerofficial',
  },
] satisfies SocialMediaData[]

export type BannerData = {
  sectionTagData?: SectionTagData
  chipsData?: ChipData[]
  heading: string | ReactNode
  description?: string
  actionButtonData?: ActionButtonData[]
  imageData?: ImageData
  label?: string
  bulletPointsData?: string[]
  standardCardData?: StandardCardData[]
  document?: DocumentRendererData
  selectForm?: DynamicFormRendererData
}

export type BannerProps = BannerData & {
  csx?: CustomStyles<StylesClassNames>
  breadcrumbsNavPathData?: BreadcrumbsNavPath[]
  showDivider?: 'top' | 'bottom'
  hideBreadcrumb?: boolean
  alternateBg?: boolean
  showLabel?: boolean
  disablePadding?: boolean
  sectionWrapperProps?: Partial<SectionWrapperProps>
  children?: ReactNode
  cardType?: CardTypes
  cardWrapperStyle?: SxProps
  glowSx?: GlowProps['sx']
  showButton?: boolean
  isThankYou?: boolean
  sectionHeaderView?: boolean
  isBlogPage?: boolean
  componentVariant?: 'h1' | 'h2' | 'h4' | 'p'
  statsCardData?: StatsCardData[]
  shouldCloudView?: boolean
  showGlow?: boolean
  showFormComponent?: boolean
  socialMediaData?: SocialMediaData[]
  showReachOutForm?: boolean
  resourceTypePage?: RESOURCE_TYPE
  showNeedHelpSection?: boolean
}

export function Banner({
  sectionTagData,
  heading,
  description,
  csx,
  actionButtonData,
  imageData,
  showDivider,
  hideBreadcrumb,
  alternateBg = true,
  label,
  showLabel = false,
  sectionWrapperProps,
  disablePadding = false,
  children,
  glowSx,
  showButton = true,
  isThankYou = false,
  sectionHeaderView = false,
  componentVariant = 'h1',
  isBlogPage = false,
  cardType = 'StatsCard',
  cardWrapperStyle,
  shouldCloudView = true,
  showGlow = false,
  showFormComponent = false,
  socialMediaData = socials,
  showReachOutForm = false,
  standardCardData,
  document,
  resourceTypePage = RESOURCE_TYPE.Blogs,
  selectForm,
}: BannerProps) {
  const styles = getStyles(defaultStyles, csx)

  const trimStatsCardData = standardCardData

  const { ref, ...resSectionWrapperProps } = sectionWrapperProps || {}

  return (
    <Box {...styles('relativeWrapper')}>
      <SectionWrapper
        flexCol={{ xs: '32px', md: '40px' }}
        showDivider={showDivider}
        disablePadding={disablePadding}
        ref={ref as LegacyRef<HTMLDivElement> | undefined}
        {...styles('wrapper')}
        glowSx={glowSx}
        {...resSectionWrapperProps}
        alternateBg={alternateBg}
        showGlow={showGlow}
      >
        {!isBlogPage && shouldCloudView && (
          <Stack {...styles('chip')}>
            <ActionButton
              variant="outlined"
              showArrow
              link={`${portalUrl}live-demo`}
              newTab
              startIcon={
                <Box
                  sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}
                >
                  <Chip
                    icon={
                      <Icon
                        iconName="StarAi"
                        colorValues="blue.700"
                        currentValue={false}
                      />
                    }
                    label="Take Tour"
                    size="small"
                    color="primary"
                    variant="trans"
                    sx={{ borderRadius: '50px', paddingInline: '2px' }}
                  />
                  <Box
                    sx={{
                      height: '100%',
                      width: '1px',
                      backgroundColor: 'border.dark',
                      display: {
                        xs: 'none',
                        sm: 'block',
                      },
                    }}
                  />
                </Box>
              }
              sx={{
                backgroundColor: 'neutral.white !important',
                borderRadius: '50px',
              }}
            >
              <Typography
                variant="caption1.700"
                color="text.secondary"
                sx={{
                  display: {
                    xs: 'none',
                    sm: 'block',
                  },
                }}
              >
                Smarter cost control, powered by Agentic AI
              </Typography>
            </ActionButton>
          </Stack>
        )}
        <Stack
          direction={{
            xs: 'column',
            md: 'row',
          }}
          width="100%"
          gap={{
            xs: '24px',
            md: '0px',
          }}
        >
          <Box {...styles('infoWrapper')}>
            {!hideBreadcrumb && <BreadcrumbsNav />}
            {sectionTagData && <SectionTag {...sectionTagData} />}

            {label && showLabel && (
              <Box>
                <Chip
                  label={<Typography variant="body3.500">{label}</Typography>}
                  color={!showGlow ? 'secondary' : 'primary'}
                  variant="filled"
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#ffffff10',
                    padding: '12px 0px !important',
                    ':hover': {
                      backgroundColor: !alternateBg
                        ? '#ffffff10 !important'
                        : 'unset',
                      color: !alternateBg ? 'common.white' : 'common.black',
                    },
                    border: !alternateBg ? 'unset' : '1px solid',
                    borderColor: !alternateBg ? 'unset' : `border.light`,
                    color: !alternateBg ? 'common.white' : 'common.black',
                    '&:hover': {
                      color: `${!alternateBg ? 'common.white' : 'common.black'}`,
                    },
                  }}
                />
              </Box>
            )}

            {sectionHeaderView ? (
              <SectionHeader
                heading={heading}
                description={description}
                actionButtonData={actionButtonData?.at(-1)}
                darkBg={!alternateBg}
                csx={{
                  description: styles('sectionHeaderDesc').sx,
                  infoWrapper: styles('sectionHeaderInfoWrapper').sx,
                  heading: styles('heading').sx,
                }}
              />
            ) : (
              <>
                <Stack
                  spacing={2.5}
                  sx={{ alignItems: 'center', width: '100%' }}
                >
                  <Typography
                    variant="h3.700"
                    color={alternateBg ? 'common.white' : 'text.primary'}
                    sx={{
                      maxWidth: { md: '80%', xs: '100%' },
                      ...styles('heading').sx,
                      ...(isThankYou && {
                        marginTop: '-24px',
                      }),
                    }}
                    component={componentVariant}
                  >
                    {heading}
                  </Typography>

                  {description && (
                    <Typography
                      color={alternateBg ? 'common.white' : 'text.secondary'}
                      variant="body1.400"
                      sx={{ maxWidth: '980px', ...styles('description').sx }}
                    >
                      {description}
                    </Typography>
                  )}
                </Stack>
                {(actionButtonData?.length ?? 0) > 0 && showButton && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {actionButtonData
                      ?.sort((a, b) =>
                        (b.createdAt || '').localeCompare(a.createdAt || '')
                      )
                      .map((item, idx) => {
                        const isEven = idx % 2 === 0
                        return (
                          <ActionButton
                            key={idx}
                            {...item}
                            {...(isEven
                              ? { variant: 'contained' }
                              : { variant: 'outlined' })}
                            {...(isEven && { showArrow: true })}
                          />
                        )
                      })}
                  </Box>
                )}
              </>
            )}
            {document && <DocumentRenderer {...document} />}

            {showFormComponent && (
              <Stack spacing={3}>
                <FormComponent />

                <Box sx={commonStyles.flexRow(2.5, 'center')}>
                  <Typography variant="body1.500" color="common.white">
                    Follow us on:
                  </Typography>
                  <Box sx={commonStyles.flexRow(1.75, 'center')}>
                    {socialMediaData.map((item, idx) => (
                      <LinkClick link={item.link} newTab key={idx}>
                        <Image
                          {...item.imageData}
                          sx={{ width: '20px', height: '20px' }}
                        />
                      </LinkClick>
                    ))}
                  </Box>
                </Box>
              </Stack>
            )}
          </Box>
          {showReachOutForm && (
            <ReachOutForm
              wrapperSx={{
                minWidth: {
                  xs: '100%',
                  md: '416px',
                },
              }}
            />
          )}
        </Stack>

        {imageData && (
          <Image
            preload
            fetchPriority="high"
            {...imageData}
            {...styles('image')}
          />
        )}
        {children}
      </SectionWrapper>

      {!!trimStatsCardData?.length && (
        <>
          <MobileOnlyCardsSlider
            statsCardData={trimStatsCardData}
            cardType={cardType}
            isBlogPage={isBlogPage}
            cardWrapperStyle={cardWrapperStyle}
          />
          <Box
            {...styles('cardsWrapper', {
              display: {
                xs: 'none',
                md: 'flex',
              },
            })}
          >
            {trimStatsCardData.map((item, idx) => (
              <Stack {...styles('cardWrapper')} spacing={2} key={idx}>
                <StandardCard
                  {...item}
                  csx={{
                    card: {
                      flex: 1,
                      ...cardWrapperStyle,
                    },
                  }}
                  resourceTypePage={resourceTypePage}
                />
              </Stack>
            ))}
          </Box>
        </>
      )}
    </Box>
  )
}
